import { useState } from 'react';
import { Loader2, Sparkles, TrendingUp, AlertTriangle, Lightbulb, Target } from 'lucide-react';
import Button from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import api from '../lib/api';

export interface JobInsightsReport {
  executiveSummary: string;
  performanceForecast: {
    expectedAcceptanceRate: number;
    estimatedCompletionDays: number;
    reachEstimate: string;
    confidence: 'high' | 'medium' | 'low';
  };
  bottlenecks: Array<{
    stage: string;
    severity: 'high' | 'medium' | 'low';
    description: string;
    recommendation: string;
  }>;
  kolTrends: Array<{
    kolName: string;
    trend: 'rising' | 'stable' | 'declining';
    insight: string;
  }>;
  optimizations: Array<{ title: string; impact: string; action: string }>;
  risks: string[];
  demo?: boolean;
  cached?: boolean;
  model?: string;
}

export interface AgencyInsightsReport {
  executiveSummary: string;
  portfolioHealth: { score: number; summary: string };
  topOpportunities: string[];
  underutilizedKols: Array<{ name: string; reason: string }>;
  campaignTrends: string[];
  recommendations: string[];
  demo?: boolean;
  cached?: boolean;
  model?: string;
}

interface AIInsightsPanelProps {
  mode: 'job' | 'agency';
  jobId?: string;
  title?: string;
  className?: string;
}

function severityVariant(severity: string): 'destructive' | 'warning' | 'secondary' {
  if (severity === 'high') return 'destructive';
  if (severity === 'medium') return 'warning';
  return 'secondary';
}

function trendColor(trend: string): string {
  if (trend === 'rising') return 'text-green-600';
  if (trend === 'declining') return 'text-red-600';
  return 'text-gray-600';
}

export function AIInsightsPanel({
  mode,
  jobId,
  title = mode === 'job' ? 'Predictive Campaign Insights' : 'Agency Portfolio Insights',
  className = '',
}: AIInsightsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobReport, setJobReport] = useState<JobInsightsReport | null>(null);
  const [agencyReport, setAgencyReport] = useState<AgencyInsightsReport | null>(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === 'job') {
        if (!jobId) throw new Error('Job ID is required');
        const res = await api.post('/ai/analytics/job-insights', { jobId });
        setJobReport(res.data);
      } else {
        const res = await api.post('/ai/analytics/agency-insights', {});
        setAgencyReport(res.data);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to generate insights';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const report = mode === 'job' ? jobReport : agencyReport;

  return (
    <Card className={`border-blue-200/60 bg-blue-50/30 dark:bg-blue-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          {title}
        </CardTitle>
        <CardDescription>
          AI-powered forecasts, bottleneck detection, and optimization recommendations from your campaign data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runAnalysis} disabled={loading} size="sm">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate AI Insights
              </>
            )}
          </Button>
          {report?.demo && <Badge variant="outline">Demo mode</Badge>}
          {report?.cached && <Badge variant="outline">Cached</Badge>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {jobReport && mode === 'job' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">{jobReport.executiveSummary}</p>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3 bg-white/50 dark:bg-black/10">
                <p className="text-xs text-muted-foreground">Expected acceptance</p>
                <p className="text-xl font-bold text-blue-600">{jobReport.performanceForecast.expectedAcceptanceRate}%</p>
              </div>
              <div className="rounded-lg border p-3 bg-white/50 dark:bg-black/10">
                <p className="text-xs text-muted-foreground">Est. completion</p>
                <p className="text-xl font-bold">{jobReport.performanceForecast.estimatedCompletionDays} days</p>
              </div>
              <div className="rounded-lg border p-3 bg-white/50 dark:bg-black/10">
                <p className="text-xs text-muted-foreground">Reach estimate</p>
                <p className="text-sm font-medium">{jobReport.performanceForecast.reachEstimate}</p>
                <Badge variant="outline" className="mt-1 text-xs">
                  {jobReport.performanceForecast.confidence} confidence
                </Badge>
              </div>
            </div>

            {jobReport.bottlenecks.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" /> Bottlenecks
                </p>
                <div className="space-y-2">
                  {jobReport.bottlenecks.map((b, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{b.stage}</span>
                        <Badge variant={severityVariant(b.severity)}>{b.severity}</Badge>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">{b.description}</p>
                      <p className="text-blue-700 dark:text-blue-300 mt-1">→ {b.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {jobReport.optimizations.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <Lightbulb className="h-4 w-4 text-yellow-600" /> Optimizations
                </p>
                <ul className="space-y-2">
                  {jobReport.optimizations.map((o, i) => (
                    <li key={i} className="rounded-lg border p-3 text-sm">
                      <p className="font-medium">{o.title}</p>
                      <p className="text-green-700 dark:text-green-400">{o.impact}</p>
                      <p className="text-gray-600 dark:text-gray-400">{o.action}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {jobReport.kolTrends.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">KOL trends</p>
                <div className="flex flex-wrap gap-2">
                  {jobReport.kolTrends.map((k, i) => (
                    <div key={i} className="rounded-lg border px-3 py-2 text-sm">
                      <span className="font-medium">{k.kolName}</span>
                      <span className={`ml-2 text-xs font-semibold uppercase ${trendColor(k.trend)}`}>{k.trend}</span>
                      <p className="text-xs text-gray-600 mt-1">{k.insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {jobReport.risks.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Risks</p>
                <ul className="text-sm list-disc list-inside text-amber-700 dark:text-amber-300">
                  {jobReport.risks.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {agencyReport && mode === 'agency' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">{agencyReport.executiveSummary}</p>

            <div className="rounded-lg border p-4 bg-white/50 dark:bg-black/10 flex items-center gap-4">
              <div className="text-3xl font-bold text-blue-600">{agencyReport.portfolioHealth.score}</div>
              <div>
                <p className="font-medium">Portfolio health score</p>
                <p className="text-sm text-gray-600">{agencyReport.portfolioHealth.summary}</p>
              </div>
            </div>

            {agencyReport.topOpportunities.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <Target className="h-4 w-4" /> Top opportunities
                </p>
                <ul className="text-sm space-y-1 list-disc list-inside text-gray-700 dark:text-gray-300">
                  {agencyReport.topOpportunities.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>
              </div>
            )}

            {agencyReport.underutilizedKols.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Underutilized KOLs</p>
                <div className="space-y-2">
                  {agencyReport.underutilizedKols.map((k, i) => (
                    <div key={i} className="rounded-lg border p-2 text-sm">
                      <span className="font-medium">{k.name}</span>
                      <span className="text-gray-600 dark:text-gray-400"> — {k.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {agencyReport.campaignTrends.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Campaign trends</p>
                <ul className="text-sm space-y-1 list-disc list-inside text-gray-700 dark:text-gray-300">
                  {agencyReport.campaignTrends.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            )}

            {agencyReport.recommendations.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Recommendations</p>
                <ol className="text-sm space-y-1 list-decimal list-inside text-gray-700 dark:text-gray-300">
                  {agencyReport.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
